import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';
import { CreateOfferDeepLinkFormValues, createOfferDeepLinkFormSchema } from '~/validation/schemas/createOfferDeepLink';

import {
  Alert,
  Box,
  Button,
  Container,
  GridLegacy as Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import { Offer } from '@luxuryescapes/contract-svc-offer';
import * as libRegions from '@luxuryescapes/lib-regions';

import PageHeader from '~/components/Common/Elements/PageHeader';
import UserBreadcrumbs from '~/components/Common/Elements/UserBreadcrumbs';
import EmployeeUserList from '~/components/Common/EmployeeUserList';
import OfferSearchForm from '~/components/Common/Forms/OfferSearchForm';
import OfferList from '~/components/Common/OfferListContainer';

import { ROLE_ADMIN_USER } from '~/consts/roles';
import stores from '~/consts/stores';
import { UTM_MEDIUM } from '~/consts/utm';

import useCurrentUser from '~/hooks/useCurrentUser';

import OffersService from '~/services/OffersService';
import UsersService from '~/services/UsersService';
import { customerPortalHost } from '~/services/common';

const STEPS = {
  SELECT_DATES: { index: 0, label: 'Deep link details' },
  SEARCH_OFFER: { index: 1, label: 'Search Offer' },
  REVIEW: { index: 2, label: 'Review & Submit' },
  SUCCESS: { index: 3, label: 'Success' },
};

const stepLabels = Object.values(STEPS)
  .sort((a, b) => a.index - b.index)
  .map((step) => step.label);

export default function CreateOfferDeepLinkForm() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [searchString, setSearchString] = useState('');
  const [selectedOffer, setSelectedOffer] = useState<App.Offer | null>(null);
  const [selectedUser, setSelectedUser] = useState<App.User | null>(null);
  const [approverUser, setApproverUser] = useState<App.User | null>(null);
  const [deepLinkResponse, setDeepLinkResponse] = useState<Offer.OfferDeepLink>(null);
  const [submitButton, setSubmitButton] = useState<Utils.SavingState>('save changes');
  const [offerDeepLink, setOfferDeepLink] = useState<string>('');

  const history = useHistory();
  const { id_user } = useParams<{ id_user: string }>();
  const { user: currentUser } = useCurrentUser();
  const tenant = useSelector((state: App.State) => state.tenant);
  const regions = libRegions.getRegions(tenant.brand);

  useEffect(() => {
    if (!id_user) {
      return;
    }

    const fetchSelectedUser = async () => {
      UsersService.getUser(id_user, { brand: tenant.brand })
        .then((user) => {
          setSelectedUser(user);
        })
        .catch((error) => {
          console.error('Error fetching user:', error);
          setSelectedUser(null);
        });
    };

    fetchSelectedUser();
  }, [id_user, tenant.brand]);

  const { control, handleSubmit, watch, setValue, trigger } = useForm<CreateOfferDeepLinkFormValues>({
    defaultValues: {
      region: '',
      offerId: '',
      approverId: '',
      caseNumber: '',
      notes: '',
      openingReason: '',
      openingReasonOther: '',
      salesAttribution: '',
    },
    resolver: yupResolver(createOfferDeepLinkFormSchema),
  });

  const approverId = watch('approverId');
  const region = watch('region');

  useEffect(() => {
    if (!approverId) {
      return;
    }

    const fetchApproverData = async () => {
      UsersService.getUser(approverId, { brand: tenant.brand })
        .then((user) => setApproverUser(user))
        .catch((error) => {
          console.error('Error fetching approver:', error);
          setApproverUser(null);
        });
    };

    fetchApproverData();
  }, [approverId, tenant.brand]);

  const { enqueueSnackbar } = useSnackbar();

  const handleNext = async () => {
    let isValid = true;

    if (activeStep === STEPS.SELECT_DATES.index) {
      isValid = await trigger(['region', 'approverId', 'caseNumber', 'openingReason', 'salesAttribution']);
      if (watch('openingReason') === 'Other') {
        isValid = isValid && (await trigger(['openingReasonOther']));
      }
    }

    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === STEPS.SELECT_DATES.index) {
      history.goBack();
      return;
    }

    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleOfferSearch = (query: string) => {
    setSearchString(`q=${query}`);
  };

  const handleOfferSelect = (offer: App.Offer) => {
    setSelectedOffer(offer);
    setValue('offerId', offer.id_salesforce_external);
    handleNext();
  };

  const customerPortalOfferLink = useMemo(() => {
    if (selectedOffer) {
      const offerLink = `/offer/${selectedOffer.slug}/${selectedOffer.id_salesforce_external}`;
      const customerPortalUrl = `${customerPortalHost(tenant)?.replace('/au', ``)}/${region ?? 'au'}`;
      return `${customerPortalUrl}${offerLink}`;
    }
    return '';
  }, [region, selectedOffer, tenant]);

  const isButtonStateSaving = useCallback(() => {
    return submitButton === 'saving';
  }, [submitButton]);

  const getOfferDeepLink = useCallback(
    async (hash, utmMedium) => {
      try {
        const deepLink = `${customerPortalOfferLink}?offerDeepLinkHash=${hash}`;
        const fullLink = await UsersService.spoofUserWithOfferDeepLink(id_user, tenant, deepLink, utmMedium);
        setOfferDeepLink(fullLink);
        return fullLink;
      } catch (error) {
        console.error('Error fetching offer deep link:', error);
      }
    },
    [customerPortalOfferLink, id_user, tenant],
  );

  const clipboardCopyDeepLink = useCallback(
    async (hash, utmMedium) => {
      try {
        setSubmitButton('saving');
        const spoofLink = await getOfferDeepLink(hash, utmMedium);

        navigator.clipboard
          .writeText(spoofLink)
          .then(() => {
            enqueueSnackbar('Deep link copied to clipboard!', { variant: 'success' });
            setSubmitButton('saved');
          })
          .catch(() => {
            enqueueSnackbar('Failed to copy hash to clipboard', { variant: 'error' });
            setSubmitButton('failed');
          });
      } catch (error) {
        console.error('Error copying deep link:', error);
        enqueueSnackbar('Error copying deep link: ' + (error.message || 'Unknown error'), { variant: 'error' });
      }
    },
    [getOfferDeepLink, enqueueSnackbar],
  );

  const onSubmit = useCallback(
    async (data: CreateOfferDeepLinkFormValues) => {
      setSubmitButton('saving');
      const payload = {
        fk_offer_id: data.offerId,
        region: data.region,
        fk_customer_id: id_user,
        agent_email: currentUser?.email,
        approver_id: data.approverId,
        case_number: data.caseNumber,
        notes: data.notes,
        opening_reason: data.openingReason === 'Other' ? data.openingReasonOther : data.openingReason,
        sales_attribution: data.salesAttribution,
      };

      try {
        const response = await OffersService.createOfferDeepLink(data.offerId, payload);

        if (response.success) {
          setDeepLinkResponse(response.result);
          clipboardCopyDeepLink(response.result.hash, data.salesAttribution);
          setSubmitButton('saved');
          setActiveStep(STEPS.SUCCESS.index);
        } else {
          setSubmitButton('failed');
          enqueueSnackbar('Failed to create offer deep link', { variant: 'error' });
        }
      } catch (e) {
        setSubmitButton('failed');
        console.error('Error creating deep link:', e);
        enqueueSnackbar('Error creating offer deep link: ' + (e.message || 'Unknown error'), { variant: 'error' });
      }
    },
    [id_user, currentUser?.email, clipboardCopyDeepLink, enqueueSnackbar],
  );

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= activeStep) {
      setActiveStep(stepIndex);
    }
  };

  const isSubmitStep = activeStep === STEPS.REVIEW.index;

  const handleApproverChange = (employeeId: string) => {
    setValue('approverId', employeeId);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case STEPS.SELECT_DATES.index:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Controller
                name="region"
                control={control}
                rules={{ required: 'Region is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    select
                    label="Region"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.code} value={region.code}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="approverId"
                control={control}
                rules={{ required: 'Approver is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <EmployeeUserList
                      user={{ preferred_agent_id: field.value } as App.User}
                      isAdmin
                      onUpdatePreferredAgent={handleApproverChange}
                      placeholder="Approver"
                      filterRoles={[ROLE_ADMIN_USER]}
                    />
                    {error && (
                      <Typography color="error" variant="caption">
                        {error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="caseNumber"
                control={control}
                rules={{ required: 'Case Number is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Case Number"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                    required
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="salesAttribution"
                control={control}
                rules={{ required: 'Sales Attribution is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    select
                    label="Sales Attribution"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                    required
                    error={!!error}
                    helperText={error?.message}
                  >
                    <MenuItem value={UTM_MEDIUM}>Contact Centre</MenuItem>
                    <MenuItem value={stores.chadstone_store.utmMedium}>{stores.chadstone_store.displayName}</MenuItem>
                    <MenuItem value={stores.bondi_store.utmMedium}>{stores.bondi_store.displayName}</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="openingReason"
                control={control}
                rules={{ required: 'Opening Reason is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    select
                    label="Opening Reason"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                    required
                    error={!!error}
                    helperText={error?.message}
                  >
                    <MenuItem value="Quote">Quote</MenuItem>
                    <MenuItem value="Booking">Booking</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            {watch('openingReason') === 'Other' && (
              <Grid item xs={12} md={4}>
                <Controller
                  name="openingReasonOther"
                  control={control}
                  rules={{ required: 'Please specify the opening reason' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Specify Opening Reason"
                      value={field.value}
                      onChange={field.onChange}
                      fullWidth
                      required
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} md={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Notes"
                    value={field.value}
                    onChange={field.onChange}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case STEPS.SEARCH_OFFER.index:
        return (
          <Box>
            <OfferSearchForm destinations={[]} searchQuery={handleOfferSearch} minimal />
            <OfferList searchString={searchString} onRowClick={handleOfferSelect} showCountMessage />
          </Box>
        );

      case STEPS.REVIEW.index:
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom align="center">
              Review Deep Link Details
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Schedule Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Region:
                        </Typography>
                        <Typography variant="body2">
                          {regions.find((r) => r.code === watch('region'))?.name || watch('region')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                            Offer ID:
                          </Typography>
                          <Typography variant="body2">
                            <Link to={`/offers/${selectedOffer.id_salesforce_external}`} target="_blank">
                              {selectedOffer.id_salesforce_external}
                            </Link>
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                            Offer:
                          </Typography>
                          <Typography variant="body2">{selectedOffer?.name || 'Not selected'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Approver ID:
                        </Typography>
                        <Typography variant="body2">{approverUser?.fullName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Agent Email:
                        </Typography>
                        <Typography variant="body2">{currentUser?.email}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Case Number:
                        </Typography>
                        <Typography variant="body2">{watch('caseNumber')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Opening Reason:
                        </Typography>
                        <Typography variant="body2">
                          {watch('openingReason') === 'Other' ? watch('openingReasonOther') : watch('openingReason')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Sales Attribution:
                        </Typography>
                        <Typography variant="body2">{watch('salesAttribution')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Notes:
                        </Typography>
                        <Typography variant="body2">{watch('notes')}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Customer Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Customer ID:
                        </Typography>
                        <Typography variant="body2">{id_user}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Customer Name:
                        </Typography>
                        <Typography variant="body2">{selectedUser?.fullName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Customer Email:
                        </Typography>
                        <Typography variant="body2">{selectedUser?.email}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        );

      case STEPS.SUCCESS.index:
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom align="center">
              Deep Link Created Successfully!
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Deep Link Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Offer ID:
                        </Typography>
                        <Typography variant="body2">
                          <Link to={`/offers/${deepLinkResponse?.offerId}`} target="_blank">
                            {deepLinkResponse?.offerId}
                          </Link>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Region:
                        </Typography>
                        <Typography variant="body2">
                          {regions.find((r) => r.code === deepLinkResponse?.region)?.name || deepLinkResponse?.region}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Status:
                        </Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {deepLinkResponse?.status}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Customer Email:
                        </Typography>
                        <Typography variant="body2">{selectedUser?.email}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Agent email:
                        </Typography>
                        <Typography variant="body2">{deepLinkResponse?.agentEmail}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Approver:
                        </Typography>
                        <Typography variant="body2">{approverUser?.fullName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Case Number:
                        </Typography>
                        <Typography variant="body2">{deepLinkResponse?.caseNumber}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px' }}>
                          Notes:
                        </Typography>
                        <Typography variant="body2">{deepLinkResponse?.notes}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Deep Link Hash
                    </Typography>
                    <Stack spacing={2} direction="column" sx={{ mb: 2 }}>
                      <Alert severity="warning">Extreme caution, paste into incognito only</Alert>
                      <Alert severity="info">Your link has been copied to the clipboard!</Alert>
                    </Stack>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: 'grey.100',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        maxHeight: '150px',
                        overflow: 'auto',
                      }}
                    >
                      {offerDeepLink}
                    </Paper>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={isButtonStateSaving()}
                        onClick={() => clipboardCopyDeepLink(deepLinkResponse?.hash, watch('salesAttribution'))}
                      >
                        Copy Deep Link
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" onClick={() => history.push(`/users/${id_user}`)}>
                Return to User Profile
              </Button>
            </Box>
          </Paper>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Book Offer</title>
      </Helmet>

      <UserBreadcrumbs
        userId={id_user}
        user={
          selectedUser
            ? { label: selectedUser.fullName || selectedUser.email || 'User', href: `/users/${id_user}` }
            : null
        }
        currentPage="Offer deep link"
      />
      <PageHeader title="Book Offer" />

      <Box sx={{ width: '100%', mt: 4 }}>
        <Stepper activeStep={activeStep}>
          {stepLabels.map((label, index) => (
            <Step
              key={label}
              completed={index < activeStep}
              onClick={() => handleStepClick(index)}
              sx={{
                cursor: index <= activeStep || index === activeStep + 1 ? 'pointer' : 'default',
                '& .MuiStepLabel-root': {
                  cursor: index <= activeStep || index === activeStep + 1 ? 'pointer' : 'default',
                },
              }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4 }}>
          <form>
            {renderStepContent(activeStep)}

            {activeStep !== STEPS.SUCCESS.index && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                {activeStep >= 0 && activeStep !== STEPS.SUCCESS.index && (
                  <Button onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                )}
                {!isSubmitStep ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!watch('offerId') && activeStep === STEPS.SEARCH_OFFER.index}
                  >
                    Next
                  </Button>
                ) : (
                  <Button disabled={isButtonStateSaving()} variant="contained" onClick={handleSubmit(onSubmit)}>
                    {submitButton}
                  </Button>
                )}
              </Box>
            )}
          </form>
        </Box>
      </Box>
    </Container>
  );
}
